export type SortDir = 'ASC' | 'DESC';

const flip = (dir: SortDir): SortDir => (dir === 'ASC' ? 'DESC' : 'ASC');

/**
 * Header click: a newly picked column opens in the direction it is normally read (dates
 * newest-first, names A-Z), while re-clicking the active column flips it. A missing direction
 * counts as not-yet-flipped, so a hand-edited URL still opens on the natural direction.
 */
export const nextSortDir = (
    isActiveField: boolean,
    activeDir: string | null,
    defaultDir: SortDir
): SortDir => (isActiveField && activeDir === defaultDir ? flip(defaultDir) : defaultDir);

export type TableSort<Field extends string> = { field: Field; dir: SortDir };

/**
 * Reads the `sort_by`/`sort_dir` pair `SortBy` writes to the URL and validates the field against
 * the set its endpoint accepts, since a hand-edited or stale-bookmarked one would be rejected by
 * the backend. Returns null when there is no usable sort, leaving it to the caller to decide what
 * unsorted means for its endpoint. `defaultDir` covers a URL naming a field but no direction.
 */
export const parseTableSort = <Field extends string>(
    searchParams: URLSearchParams,
    fields: readonly Field[],
    defaultDir: (field: Field) => SortDir
): TableSort<Field> | null => {
    const field = searchParams.get('sort_by') as Field | null;
    if (!field || !fields.includes(field)) return null;

    const dir = searchParams.get('sort_dir');

    return { field, dir: dir === 'ASC' || dir === 'DESC' ? dir : defaultDir(field) };
};
